import { Form } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { masterclassPath } from "@/routes"

/**
 * OKS-54 — Delete-confirm AlertDialog (UX Spec §4 + Show §2.2).
 *
 * Hard-delete (PRD §3 — no soft-delete column in V1; no downstream
 * rows yet). Wraps Inertia `<Form method="delete">`.
 *
 * UX Spec §6 a11y: default focus on Cancel (the safer action), the
 * destructive button requires deliberate tab. Distinct from
 * publish/unpublish which default-focus the primary action.
 */
export interface MasterclassDeleteConfirmDialogProps {
  masterclassId: number
}

export function MasterclassDeleteConfirmDialog({
  masterclassId,
}: MasterclassDeleteConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button
            variant="destructive"
            data-testid="masterclass-delete-trigger"
          >
            <Trash2 className="mr-1 size-4" aria-hidden="true" />
            {t("masterclasses.show.actions.delete")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="masterclass-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("masterclasses.confirm.delete.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("masterclasses.confirm.delete.body")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={masterclassPath(masterclassId)}
          method="delete"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                autoFocus
                data-testid="masterclass-delete-cancel"
              >
                {t("masterclasses.confirm.delete.cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={processing}
                data-testid="masterclass-delete-confirm"
              >
                {t("masterclasses.confirm.delete.confirm")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
